// forms/RemovalsForm.tsx
import React, { useState, useEffect} from 'react';
import { BaseFormProps, RemovalsFormData } from '../../../types/FormTypes';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useTokenStore from '../../../store/store';
import { 
  faTruck, 
  faHome,
  faMapMarkerAlt,
  faStickyNote
} from '@fortawesome/free-solid-svg-icons';
import { 
  User, 
  AlertTriangle,
  Loader2,
  Edit
} from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/semantic-ui.css';
import { fetchUserProfile } from '../../../utils/user';

interface RemovalsFormProps extends BaseFormProps<RemovalsFormData> {}

const RemovalsForm: React.FC<RemovalsFormProps> = ({
  initialData,
  errors = {},
  isSubmitting = false,
  onSubmit,
  onCancel,
  themes
}) => {
  // Default form structure
  const getDefaultData = (): RemovalsFormData => ({
    clientDetails: {
      title: '',
      firstName: '',
      lastName: '',
      mobile: '',
      email: ''
    },
    clientAddress: {
      address1: '',
      address2: '',
      address3: '',
      town: '',
      postcode: ''
    },
    collectionAddress: {
      address1: '',
      address2: '',
      address3: '',
      town: '',
      postcode: '',
      bedrooms: '',
      movingFrom: '',
      propertyValue: ''
    },
    deliveryAddress: {
      address1: '',
      address2: '',
      address3: '',
      town: '',
      postcode: '',
      storageRequired: false,
      expectedRemovalDate: ''
    },
    additionalNotes: {
      notes: ''
    }
  });

  // Local form state
  const [formData, setFormData] = useState<RemovalsFormData>(() => ({
    ...getDefaultData(),
    ...initialData
  }));
  
  const [currentStep, setCurrentStep] = useState(1);
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  // Sync with parent data changes
  useEffect(() => {
    if (initialData) {
      setFormData((prev: RemovalsFormData) => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  // Fetch and auto-populate user profile data
  useEffect(() => {
    const getToken = useTokenStore.getState().token; // Get token from store
    
    const loadUserProfile = async () => {
      try {
        if (!getToken) {
          console.log('No token available for user profile fetch');
          return;
        }
        
        const userProfile = await fetchUserProfile(getToken); // Pass token to fetchUserProfile
        if (userProfile) {
          console.log('Auto-populating removals form with user profile:', userProfile);
          setFormData((prev: RemovalsFormData) => ({
            ...prev,
            clientDetails: {
              ...prev.clientDetails,
              firstName: userProfile.firstName || prev.clientDetails.firstName,
              lastName: userProfile.lastName || prev.clientDetails.lastName,
              email: userProfile.email || prev.clientDetails.email,
              mobile: userProfile.phone || prev.clientDetails.mobile,
            }
          }));
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
      }
    };
  
    // Only fetch if we don't already have user data and we have a token
    const hasUserData = formData.clientDetails.firstName || 
                       formData.clientDetails.lastName || 
                       formData.clientDetails.email || 
                       formData.clientDetails.mobile;
    
    if (!hasUserData && getToken) {
      loadUserProfile();
    }
  }, [
    formData.clientDetails.firstName,
    formData.clientDetails.lastName,
    formData.clientDetails.email,
    formData.clientDetails.mobile
  ]);

  // Validation
  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    
    switch (step) {
      case 1:
        const { title, firstName, lastName, mobile, email } = formData.clientDetails;
        if (!title.trim()) newErrors['client.title'] = 'Title is required';
        if (!firstName.trim()) newErrors['client.firstName'] = 'First name is required';
        if (!lastName.trim()) newErrors['client.lastName'] = 'Last name is required';
        if (!mobile.trim() || mobile.length < 10) newErrors['client.mobile'] = 'Valid mobile number is required';
        if (!email.trim() || !email.includes('@')) newErrors['client.email'] = 'Valid email is required';
        break;
      case 2:
        const { address1, town, postcode } = formData.clientAddress;
        if (!address1.trim()) newErrors['clientAddress.address1'] = 'Address 1 is required';
        if (!town.trim()) newErrors['clientAddress.town'] = 'Town is required';
        if (!postcode.trim()) newErrors['clientAddress.postcode'] = 'Postcode is required';
        break;
      case 3:
        const { address1: collAddress1, town: collTown, postcode: collPostcode, bedrooms, movingFrom } = formData.collectionAddress;
        if (!collAddress1.trim()) newErrors['collectionAddress.address1'] = 'Address 1 is required';
        if (!collTown.trim()) newErrors['collectionAddress.town'] = 'Town is required';
        if (!collPostcode.trim()) newErrors['collectionAddress.postcode'] = 'Postcode is required';
        if (!bedrooms.trim()) newErrors['collectionAddress.bedrooms'] = 'Number of bedrooms is required';
        if (!movingFrom.trim()) newErrors['collectionAddress.movingFrom'] = 'Moving from is required';
        break;
      case 4:
        const { address1: delAddress1, town: delTown, postcode: delPostcode } = formData.deliveryAddress;
        if (!delAddress1.trim()) newErrors['deliveryAddress.address1'] = 'Address 1 is required';
        if (!delTown.trim()) newErrors['deliveryAddress.town'] = 'Town is required';
        if (!delPostcode.trim()) newErrors['deliveryAddress.postcode'] = 'Postcode is required';
        break;
      case 5:
        // Notes step - no validation needed
        break;
      case 6:
        // Review step - no validation needed
        break;
    }
    
    setLocalErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form field updater
  const updateField = (path: string, value: any) => {
    setFormData((prev: RemovalsFormData) => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current: any = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (!current[key]) current[key] = {};
        current = current[key];
      }
      
      const lastKey = keys[keys.length - 1];
      current[lastKey] = value;
      
      return newData;
    });
  };

  // Navigation
  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 6));
      const formContainer = document.querySelector('.flex-1.overflow-y-auto');
      if (formContainer) {
        formContainer.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
    if (formContainer) {
      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Submit
  const handleSubmit = () => {
    if (validateStep(currentStep)) {
      const requiresPayment = false; // Removals typically don't require immediate payment
      onSubmit(formData, requiresPayment);
    }
  };

  // Combine local and parent errors
  const allErrors = { ...localErrors, ...errors };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <FontAwesomeIcon icon={faTruck} className="w-16 h-16 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Removals Service Request</h3>
              <p className="text-gray-600">Please provide your contact details</p>
            </div>

            {/* Client Details Form */}
            <div className="space-y-6">
              <h4 className="text-lg font-semibold flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                The Client
              </h4>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <select
                  value={formData.clientDetails.title}
                  onChange={(e) => updateField('clientDetails.title', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['client.title'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                >
                  <option value="">Select title</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Miss">Miss</option>
                  <option value="Ms">Ms</option>
                  <option value="Dr">Dr</option>
                  <option value="Prof">Prof</option>
                </select>
                {allErrors['client.title'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.title']}
                  </p>
                )}
              </div>

              {/* First Name */}
              <div>
                <label className="block text-sm font-medium mb-1">First Name *</label>
                <input
                  type="text"
                  value={formData.clientDetails.firstName}
                  onChange={(e) => updateField('clientDetails.firstName', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['client.firstName'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Enter your first name"
                />
                {allErrors['client.firstName'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.firstName']}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium mb-1">Last Name *</label>
                <input
                  type="text"
                  value={formData.clientDetails.lastName}
                  onChange={(e) => updateField('clientDetails.lastName', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['client.lastName'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Enter your last name"
                />
                {allErrors['client.lastName'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.lastName']}
                  </p>
                )}
              </div>

              {/* Mobile */}
              <div>
                <label className="block text-sm font-medium mb-1">Mobile *</label>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: allErrors['client.mobile'] ? '2px solid #ef4444' : '2px solid #d1d5db',
                    borderRadius: '0.5rem',
                    backgroundColor: allErrors['client.mobile'] ? '#fef2f2' : '#FFF',
                    padding: '0.25rem',
                    position: 'relative',
                  }}
                >
                  <PhoneInput
                    country={'gb'}
                    value={formData.clientDetails.mobile}
                    onChange={(phone: string) => updateField('clientDetails.mobile', phone)}
                    enableSearch={true}
                    inputStyle={{
                      border: 'none',
                      outline: 'none',
                      width: '100%',
                      fontSize: '1rem',
                      color: '#2D3748',
                      paddingLeft: '48px',
                      backgroundColor: 'transparent',
                    }}
                    inputProps={{
                      name: 'mobile',
                      required: true,
                      placeholder: 'No leading 0 after country code',
                    }}
                    buttonStyle={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      position: 'absolute',
                      left: '4px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      width: '24px',
                      height: '24px',
                    }}
                    dropdownStyle={{
                      position: 'absolute',
                      top: 'calc(100% + 20px)',
                      left: -8,
                      borderRadius: '0.375rem',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #ddd',
                      backgroundColor: '#fff',
                      width: '200px',
                    }}
                    containerStyle={{
                      position: 'relative',
                    }}
                    searchStyle={{
                      margin: '0',
                      width: '85%',
                      height: '30px',
                      textAlign: 'left',
                    }}
                    disableCountryCode={false}
                    countryCodeEditable={false}
                  />
                </div>
                {allErrors['client.mobile'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.mobile']}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.clientDetails.email}
                  onChange={(e) => updateField('clientDetails.email', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['client.email'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="you@example.com"
                />
                {allErrors['client.email'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['client.email']}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold">The Client's Address</h3>
              <p className="text-gray-600 mt-2">Please provide your current address</p>
            </div>

            <div className="space-y-6">
              <h4 className="text-lg font-semibold flex items-center gap-2">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="w-5 h-5 text-blue-600" />
                Address Details
              </h4>

              {/* Address 1 */}
              <div>
                <label className="block text-sm font-medium mb-1">Address 1 *</label>
                <input
                  type="text"
                  value={formData.clientAddress.address1}
                  onChange={(e) => updateField('clientAddress.address1', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['clientAddress.address1'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="House number and street"
                />
                {allErrors['clientAddress.address1'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['clientAddress.address1']}
                  </p>
                )}
              </div>

              {/* Address 2 */}
              <div>
                <label className="block text-sm font-medium mb-1">Address 2</label>
                <input
                  type="text"
                  value={formData.clientAddress.address2}
                  onChange={(e) => updateField('clientAddress.address2', e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                  placeholder="Apartment, suite, etc."
                />
              </div>

              {/* Address 3 */}
              <div>
                <label className="block text-sm font-medium mb-1">Address 3</label>
                <input
                  type="text"
                  value={formData.clientAddress.address3}
                  onChange={(e) => updateField('clientAddress.address3', e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                  placeholder="Additional address information"
                />
              </div>

              {/* Town */}
              <div>
                <label className="block text-sm font-medium mb-1">Town *</label>
                <input
                  type="text"
                  value={formData.clientAddress.town}
                  onChange={(e) => updateField('clientAddress.town', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['clientAddress.town'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Town or city"
                />
                {allErrors['clientAddress.town'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['clientAddress.town']}
                  </p>
                )}
              </div>

              {/* Postcode */}
              <div>
                <label className="block text-sm font-medium mb-1">Postcode *</label>
                <input
                  type="text"
                  value={formData.clientAddress.postcode}
                  onChange={(e) => updateField('clientAddress.postcode', e.target.value.toUpperCase())}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['clientAddress.postcode'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="e.g., SW1A 1AA"
                />
                {allErrors['clientAddress.postcode'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['clientAddress.postcode']}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold">Collection Address</h3>
              <p className="text-gray-600 mt-2">Where are you moving from?</p>
            </div>

            <div className="space-y-6">
              <h4 className="text-lg font-semibold flex items-center gap-2">
                <FontAwesomeIcon icon={faHome} className="w-5 h-5 text-blue-600" />
                Collection Details
              </h4>

              {/* Address 1 */}
              <div>
                <label className="block text-sm font-medium mb-1">Address 1 *</label>
                <input
                  type="text"
                  value={formData.collectionAddress.address1}
                  onChange={(e) => updateField('collectionAddress.address1', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['collectionAddress.address1'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="House number and street"
                />
                {allErrors['collectionAddress.address1'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['collectionAddress.address1']}
                  </p>
                )}
              </div>

              {/* Address 2 */}
              <div>
                <label className="block text-sm font-medium mb-1">Address 2</label>
                <input
                  type="text"
                  value={formData.collectionAddress.address2}
                  onChange={(e) => updateField('collectionAddress.address2', e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                  placeholder="Apartment, suite, etc."
                />
              </div>

              {/* Address 3 */}
              <div>
                <label className="block text-sm font-medium mb-1">Address 3</label>
                <input
                  type="text"
                  value={formData.collectionAddress.address3}
                  onChange={(e) => updateField('collectionAddress.address3', e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                  placeholder="Additional address information"
                />
              </div>

              {/* Town */}
              <div>
                <label className="block text-sm font-medium mb-1">Town *</label>
                <input
                  type="text"
                  value={formData.collectionAddress.town}
                  onChange={(e) => updateField('collectionAddress.town', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['collectionAddress.town'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Town or city"
                />
                {allErrors['collectionAddress.town'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['collectionAddress.town']}
                  </p>
                )}
              </div>

              {/* Postcode */}
              <div>
                <label className="block text-sm font-medium mb-1">Postcode *</label>
                <input
                  type="text"
                  value={formData.collectionAddress.postcode}
                  onChange={(e) => updateField('collectionAddress.postcode', e.target.value.toUpperCase())}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['collectionAddress.postcode'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="e.g., SW1A 1AA"
                />
                {allErrors['collectionAddress.postcode'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['collectionAddress.postcode']}
                  </p>
                )}
              </div>

              {/* Bedrooms */}
              <div>
                <label className="block text-sm font-medium mb-1">Bedrooms *</label>
                <select
                  value={formData.collectionAddress.bedrooms}
                  onChange={(e) => updateField('collectionAddress.bedrooms', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['collectionAddress.bedrooms'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                >
                  <option value="">Select number of bedrooms</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5 or more">5 or more</option>
                </select>
                {allErrors['collectionAddress.bedrooms'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['collectionAddress.bedrooms']}
                  </p>
                )}
              </div>

              {/* Moving From */}
              <div>
                <label className="block text-sm font-medium mb-1">Moving From *</label>
                <select
                  value={formData.collectionAddress.movingFrom}
                  onChange={(e) => updateField('collectionAddress.movingFrom', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['collectionAddress.movingFrom'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                >
                  <option value="">Select option</option>
                  <option value="Property I Rent">Property I Rent</option>
                  <option value="Property I Own">Property I Own</option>
                </select>
                {allErrors['collectionAddress.movingFrom'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['collectionAddress.movingFrom']}
                  </p>
                )}
              </div>

              {/* Property Value */}
              <div>
                <label className="block text-sm font-medium mb-1">Property Value</label>
                <input
                  type="text"
                  value={formData.collectionAddress.propertyValue}
                  onChange={(e) => updateField('collectionAddress.propertyValue', e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                  placeholder="e.g., £250,000"
                />
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold">Delivery Address</h3>
              <p className="text-gray-600 mt-2">Where are you moving to?</p>
            </div>

            <div className="space-y-6">
              <h4 className="text-lg font-semibold flex items-center gap-2">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="w-5 h-5 text-blue-600" />
                Delivery Details
              </h4>

              {/* Address 1 */}
              <div>
                <label className="block text-sm font-medium mb-1">Address 1 *</label>
                <input
                  type="text"
                  value={formData.deliveryAddress.address1}
                  onChange={(e) => updateField('deliveryAddress.address1', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['deliveryAddress.address1'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="House number and street"
                />
                {allErrors['deliveryAddress.address1'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['deliveryAddress.address1']}
                  </p>
                )}
              </div>

              {/* Address 2 */}
              <div>
                <label className="block text-sm font-medium mb-1">Address 2</label>
                <input
                  type="text"
                  value={formData.deliveryAddress.address2}
                  onChange={(e) => updateField('deliveryAddress.address2', e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                  placeholder="Apartment, suite, etc."
                />
              </div>

              {/* Address 3 */}
              <div>
                <label className="block text-sm font-medium mb-1">Address 3</label>
                <input
                  type="text"
                  value={formData.deliveryAddress.address3}
                  onChange={(e) => updateField('deliveryAddress.address3', e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                  placeholder="Additional address information"
                />
              </div>

              {/* Town */}
              <div>
                <label className="block text-sm font-medium mb-1">Town *</label>
                <input
                  type="text"
                  value={formData.deliveryAddress.town}
                  onChange={(e) => updateField('deliveryAddress.town', e.target.value)}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['deliveryAddress.town'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="Town or city"
                />
                {allErrors['deliveryAddress.town'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['deliveryAddress.town']}
                  </p>
                )}
              </div>

              {/* Postcode */}
              <div>
                <label className="block text-sm font-medium mb-1">Postcode *</label>
                <input
                  type="text"
                  value={formData.deliveryAddress.postcode}
                  onChange={(e) => updateField('deliveryAddress.postcode', e.target.value.toUpperCase())}
                  className={`w-full p-3 border-2 rounded-lg transition-colors ${
                    allErrors['deliveryAddress.postcode'] ? 'border-red-500 bg-red-50' : 'border-gray-300 focus:border-blue-500'
                  }`}
                  placeholder="e.g., SW1A 1AA"
                />
                {allErrors['deliveryAddress.postcode'] && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {allErrors['deliveryAddress.postcode']}
                  </p>
                )}
              </div>

              {/* Storage Required */}
              <div>
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.deliveryAddress.storageRequired}
                    onChange={(e) => updateField('deliveryAddress.storageRequired', e.target.checked)}
                    className="w-4 h-4 rounded border-2 transition-colors focus:ring-2 focus:ring-offset-2"
                    style={{
                      borderColor: formData.deliveryAddress.storageRequired ? (themes?.color1 || '#1E5188') : '#d1d5db',
                      backgroundColor: formData.deliveryAddress.storageRequired ? (themes?.color1 || '#1E5188') : 'transparent',
                      accentColor: themes?.color1 || '#1E5188'
                    }}
                  />
                  <span className="text-sm font-medium">Storage required?</span>
                </label>
              </div>

              {/* Expected Removal Date */}
              <div>
                <label className="block text-sm font-medium mb-1">Expected Removal Date (if known)</label>
                <input
                  type="date"
                  value={formData.deliveryAddress.expectedRemovalDate}
                  onChange={(e) => updateField('deliveryAddress.expectedRemovalDate', e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold">Additional Notes</h3>
              <p className="text-gray-600 mt-2">For example, when is the best time for us to call</p>
            </div>

            <div className="space-y-6">
              <h4 className="text-lg font-semibold flex items-center gap-2">
                <FontAwesomeIcon icon={faStickyNote} className="w-5 h-5 text-blue-600" />
                Notes
              </h4>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={formData.additionalNotes.notes}
                  onChange={(e) => updateField('additionalNotes.notes', e.target.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 transition-colors"
                  placeholder="For example, when is the best time for us to call..."
                  rows={6}
                />
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold">Review your answers</h3>
              <p className="text-gray-600 mt-2">Please review your information before proceeding</p>
            </div>

            {/* Client Details Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Client Details
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Title</label>
                  <p className="text-base">{formData.clientDetails.title}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">First Name</label>
                  <p className="text-base">{formData.clientDetails.firstName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Last Name</label>
                  <p className="text-base">{formData.clientDetails.lastName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Mobile</label>
                  <p className="text-base">{formData.clientDetails.mobile}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Email</label>
                  <p className="text-base">{formData.clientDetails.email}</p>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(1);
                    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
                    if (formContainer) {
                      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Client Details
                </button>
              </div>
            </div>

            {/* Client Address Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="w-5 h-5 text-blue-600" />
                Client Address
              </h4>
              
              <div className="space-y-2">
                <div>
                  <label className="text-sm font-medium text-gray-600">Address</label>
                  <p className="text-base">
                    {formData.clientAddress.address1}
                    {formData.clientAddress.address2 && <br />}
                    {formData.clientAddress.address2}
                    {formData.clientAddress.address3 && <br />}
                    {formData.clientAddress.address3}
                    <br />
                    {formData.clientAddress.town}
                    <br />
                    {formData.clientAddress.postcode}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(2);
                    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
                    if (formContainer) {
                      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Client Address
                </button>
              </div>
            </div>

            {/* Collection Address Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faHome} className="w-5 h-5 text-blue-600" />
                Collection Address
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Address</label>
                  <p className="text-base">
                    {formData.collectionAddress.address1}
                    {formData.collectionAddress.address2 && <br />}
                    {formData.collectionAddress.address2}
                    {formData.collectionAddress.address3 && <br />}
                    {formData.collectionAddress.address3}
                    <br />
                    {formData.collectionAddress.town}
                    <br />
                    {formData.collectionAddress.postcode}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Bedrooms</label>
                  <p className="text-base">{formData.collectionAddress.bedrooms}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Moving From</label>
                  <p className="text-base">{formData.collectionAddress.movingFrom}</p>
                </div>
                {formData.collectionAddress.propertyValue && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Property Value</label>
                    <p className="text-base">{formData.collectionAddress.propertyValue}</p>
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(3);
                    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
                    if (formContainer) {
                      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Collection Address
                </button>
              </div>
            </div>

            {/* Delivery Address Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="w-5 h-5 text-blue-600" />
                Delivery Address
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Address</label>
                  <p className="text-base">
                    {formData.deliveryAddress.address1}
                    {formData.deliveryAddress.address2 && <br />}
                    {formData.deliveryAddress.address2}
                    {formData.deliveryAddress.address3 && <br />}
                    {formData.deliveryAddress.address3}
                    <br />
                    {formData.deliveryAddress.town}
                    <br />
                    {formData.deliveryAddress.postcode}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Storage Required</label>
                  <p className="text-base">{formData.deliveryAddress.storageRequired ? 'Yes' : 'No'}</p>
                </div>
                {formData.deliveryAddress.expectedRemovalDate && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Expected Removal Date</label>
                    <p className="text-base">{formData.deliveryAddress.expectedRemovalDate}</p>
                  </div>
                )}
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep(4);
                    const formContainer = document.querySelector('.flex-1.overflow-y-auto');
                    if (formContainer) {
                      formContainer.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Delivery Address
                </button>
              </div>
            </div>

            {/* Additional Notes Section */}
            {formData.additionalNotes.notes && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon={faStickyNote} className="w-5 h-5 text-blue-600" />
                  Additional Notes
                </h4>
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Notes</label>
                  <p className="text-base">{formData.additionalNotes.notes}</p>
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentStep(5);
                      const formContainer = document.querySelector('.flex-1.overflow-y-auto');
                      if (formContainer) {
                        formContainer.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Notes
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      {/* Progress Indicator */}
      <div className="flex items-center justify-center mb-6">
        {[1, 2, 3, 4, 5, 6].map((step) => (
          <div key={step} className="flex items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                step <= currentStep
                  ? 'shadow-lg'
                  : 'bg-gray-200 text-gray-600'
              }`}
              style={{
                backgroundColor: step <= currentStep ? (themes?.color1 || '#1E5188') : undefined,
                color: step <= currentStep ? (themes?.fontColor || '#ffffff') : undefined
              }}
            >
              {step < currentStep ? '✓' : step}
            </div>
            {step < 6 && (
              <div
                className={`w-16 h-2 mx-2 rounded-full transition-all ${
                  step < currentStep ? 'shadow-sm' : 'bg-gray-200'
                }`}
                style={{
                  backgroundColor: step < currentStep ? (themes?.color1 || '#1E5188') : undefined
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Form Content - Scrollable */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {renderStep()}
      </div>

      {/* Navigation Buttons - Fixed at bottom */}
      <div className="flex justify-between pt-6 border-t bg-white">
        <button
          type="button"
          onClick={currentStep === 1 ? onCancel : handleBack}
          disabled={isSubmitting}
          className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors font-medium"
        >
          {currentStep === 1 ? 'Cancel' : '← Back'}
        </button>

        <button
          type="button"
          onClick={currentStep === 6 ? handleSubmit : handleNext}
          disabled={isSubmitting}
          className="px-6 py-3 rounded-lg disabled:opacity-50 transition-all font-bold shadow-lg hover:shadow-xl transform hover:scale-105"
          style={{ 
            backgroundColor: themes?.color1 || '#1E5188',
            color: themes?.fontColor || '#ffffff'
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = themes?.color2 || themes?.color1 || '#1E5188';
            }
          }}
          onMouseLeave={(e) => {
            if (!isSubmitting) {
              e.currentTarget.style.backgroundColor = themes?.color1 || '#1E5188';
            }
          }}
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="animate-spin h-4 w-4" />
              Processing...
            </span>
          ) : currentStep === 6 ? (
            'Confirm and Submit To Our Partner'
          ) : (
            'Next →'
          )}
        </button>
      </div>
    </div>
  );
};

export default RemovalsForm;